/**
 * Port of `src/app/(dashboard)/rent/layout.tsx`.
 *
 * The original was an async Server Component whose only server work was
 * `await auth()` to read the user's name and role for the header chip. The SPA
 * gets both from `useSession()` (the `next-auth/react` compat shim over the
 * FastAPI token session), so the component becomes a plain one and the markup
 * is copied character-for-character — same wrappers, same sticky offsets, same
 * class strings (FE-02).
 *
 * Two deliberate differences, both structural rather than visual:
 *
 *  - `{children}` becomes react-router's `<Outlet />`. This layout is a
 *    PATHLESS PARENT ROUTE of every `/rent/*` route, which is the router's
 *    equivalent of a Next.js segment layout: it mounts once and survives
 *    navigation between rent sub-pages, exactly as the Next.js one does.
 *
 *  - The original mounted its own `<Toaster />`, because no Next.js layout
 *    above it did. The SPA's `components/Providers.tsx` already mounts one
 *    app-wide with the same `position` / `richColors` / `closeButton`
 *    settings, so a second one here would render every rent toast TWICE.
 *    Omitted on purpose; the toasts themselves are unchanged.
 *
 * Access is unchanged (SAFE-04): `/rent` is gated by `PAGE_REGISTRY` +
 * `RequireAuth` above this component, and `rentModuleAccess` is enforced by
 * FastAPI's `require_rent_access` on every `/py-api/v1/*` call this subtree
 * makes. Nothing here grants or withholds anything on its own.
 */
import { Outlet } from "react-router-dom";
import { Building2 } from "lucide-react";
import { useSession } from "@/compat/next-auth-react";
import { RentSubNav } from "@/components/rent/core/SubNav";
import { ErrorBoundary } from "@/components/rent/core/ErrorBoundary";
import { AdminViewToggle, AdminViewBanner } from "@/components/rent/core/AdminViewToggle";
import { RecordPaymentFAB } from "@/components/rent/core/payment/RecordPaymentFAB";
import { cn } from "@/lib/utils";

export function RentLayout() {
  const { data: session } = useSession();
  const userName = session?.user?.name ?? "—";
  const userRole = session?.user?.role ?? "USER";
  // ADMIN always passes, mirroring `require_rent_access` on the server, which
  // treats role ADMIN as sufficient rather than requiring the flag as well.
  const hasRentAccess = userRole === "ADMIN" || session?.user?.rentModuleAccess === true;

  // TWO GATES THAT DISAGREED.
  //
  // `/rent` is reachable via the `rent` PAGE PERMISSION, but every request the
  // subtree makes is gated server-side by `rentModuleAccess` — a DIFFERENT
  // flag. A user holding the page permission without the flag (Rinky, on
  // production) got the Rent entry in her nav, navigated in, and every query
  // 403'd. Because nothing caught them, the rejections surfaced as UNCAUGHT
  // exceptions: seven on a single pass of the rent pages, "rent module access
  // required" thrown into the console with a half-rendered screen behind it.
  //
  // Checking the flag here is not a second security boundary — the server
  // remains the only thing that decides, and this cannot grant anything. It
  // exists so the answer arrives as a SENTENCE rather than as a stack trace.
  if (!hasRentAccess) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center">
        <div
          className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl text-white"
          style={{ background: "linear-gradient(135deg, var(--cp-accent), var(--cp-accent-2))" }}
        >
          <Building2 className="h-6 w-6" />
        </div>
        <h1 className="mt-4 text-lg font-bold text-[var(--cp-ink)]">
          Rent Manager is not enabled for your account
        </h1>
        <p className="mt-2 text-sm text-[var(--cp-ink-2)]">
          Property and tenancy management is limited to the family members who
          manage the rentals. An admin can enable it for you from Admin →
          Users.
        </p>
      </div>
    );
  }

  return (
    // Mobile keeps the shell's bottom padding (bottom tab bar clearance):
    // only cancel the shell's p-4 on the sides/top; -mb-4 stays md+ only.
    //
    // At `lg` the desktop rail shell gives its content NO padding and draws no
    // app bar above, so those cancelling negative margins would pull this
    // section outside its column (a horizontal scrollbar on every rent page)
    // and the header would stick 48px below where it should. Both are undone
    // at that breakpoint; `min-w-0` keeps the flex column from being sized by
    // its widest child instead of by its container.
    <div className="-mx-4 -mt-4 flex min-h-[calc(100vh-3rem)] min-w-0 flex-col bg-[var(--cp-canvas)] md:-mb-4 md:min-h-[calc(100vh-3.5rem)] lg:mx-0 lg:mb-0 lg:mt-0 lg:min-h-0">
      {/* Top header bar — sits below the sticky navy app bar (h-12 <md, h-14 md+);
          at `lg` there is no app bar, so it sticks to the top of the column. */}
      <header className="sticky top-12 z-20 border-b border-[var(--cp-line)] bg-[var(--cp-surface)]/90 backdrop-blur md:top-14 lg:top-0">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 md:px-6">
          <div className="flex items-center gap-2">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-lg text-white shadow-[var(--cp-shadow-sm)]"
              style={{ background: "linear-gradient(135deg, var(--cp-accent), var(--cp-accent-2))" }}
            >
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <div className="text-base font-bold text-[var(--cp-ink)]">Rent Manager</div>
              <div className="-mt-0.5 text-[11px] text-[var(--cp-ink-3)]">Property &amp; tenancy management</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <AdminViewToggle />
            {/* Who you are is in the desktop rail's footer at `lg`; repeating
                it here is the same fact twice on one screen. Kept below that
                breakpoint, where the phone chrome shows no name at all. */}
            <div className="hidden items-center gap-2 sm:flex lg:hidden">
              <div className="text-right">
                <div className="text-sm font-medium text-[var(--cp-ink)]">{userName}</div>
              </div>
              <span className={cn("cp-pill", userRole === "ADMIN" ? "cp-pill--prog" : "cp-pill--wait")}>
                {userRole}
              </span>
            </div>
            {/* Theme toggle removed — the global navy top bar already has one */}
          </div>
        </div>
        {/*
          Below `md` only. On md+ these same fifteen entries are the left
          sidebar (`layout/Sidebar.tsx`, fed by the `rent` domain in
          `lib/rbac/domains.ts`), and drawing both would mean two navigations
          for one module stacked on top of each other.

          Kept on small screens because there is no sidebar there — a 210px
          rail on a phone is a desktop idiom on a touch screen — so this strip
          remains the only way into Properties, Tenants and the rest.
        */}
        <div className="border-t border-[var(--cp-line-2)] px-4 py-2 md:hidden">
          <RentSubNav />
        </div>
        <AdminViewBanner />
      </header>

      <main className="flex-1">
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>

      <RecordPaymentFAB />
    </div>
  );
}
